import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, XIcon, WarningIcon } from '@phosphor-icons/react';

import { useDashboardContext } from '../context';
import { fetchDriverAppStats } from '../models';
import { Table } from 'components/table/Table';
import { DelayedSpinner } from 'components/Spinner';
import { StatCard } from 'components/stat-card/StatCard';
import { Pill } from 'components/pill/Pill';
import { Button } from 'components/button/Button';
import { SimpleSelect, ISimpleSelectItem } from 'components/select/SimpleSelect';
import { useSuppliers } from '../../supplier/models';
import type {
  DriverAppStatsOrder,
  DriverAppStatsSummary,
  DriverAppFlag,
} from '../endpoints/DriverAppStatsEndpoint.types';

function FlagBadge({ flag }: { flag: DriverAppFlag }) {
  const labels: Record<DriverAppFlag, string> = {
    SAME_LOAD_UNLOAD_TIME: 'Laden = Lossen',
    NOT_USED_APP: 'App niet gebruikt',
    NO_CMR_UPLOADED: 'Geen CMR',
  };

  return (
    <Pill color="negative" size="sm" className="mr-1">
      <WarningIcon className="w-3 h-3" weight="fill" />
      {labels[flag]}
    </Pill>
  );
}

function AppUsageIcon({ used }: { used: boolean }) {
  if (used) {
    return <CheckIcon className="w-5 h-5 text-positive-text" weight="bold" />;
  }
  return <XIcon className="w-5 h-5 text-negative-text" weight="bold" />;
}

function appUsageShare(value: number, total: number): string {
  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
  return `${percentage}%`;
}

export function DriverAppStats() {
  const { timeFrame, businessUnit } = useDashboardContext();

  const [orders, setOrders] = useState<DriverAppStatsOrder[]>([]);
  const [summary, setSummary] = useState<DriverAppStatsSummary | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<ISimpleSelectItem | null>(null);
  const [selectedTruck, setSelectedTruck] = useState<ISimpleSelectItem | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [flagFilter, setFlagFilter] = useState<'all' | 'flagged'>('all');

  const { data: suppliersData, isLoading: suppliersLoading } = useSuppliers({ isRegularCarrier: true }, 150);

  const suppliers = suppliersData ?? [];

  const supplierItems: ISimpleSelectItem[] = useMemo(
    () => suppliers.sort((a, b) => a.name.localeCompare(b.name)).map((s) => ({ key: String(s.id), name: s.name })),
    [suppliers],
  );

  const truckItems: ISimpleSelectItem[] = useMemo(() => {
    if (!selectedSupplier) return [];
    const supplier = suppliers.find((s) => String(s.id) === selectedSupplier.key);
    if (!supplier?.trucks) return [];
    return supplier.trucks.filter((t) => t.isActive).map((t) => ({ key: t.licensePlate, name: t.licensePlate }));
  }, [selectedSupplier, suppliers]);

  useEffect(() => {
    setSelectedTruck(null);
  }, [selectedSupplier]);

  useEffect(() => {
    if (!timeFrame?.startDate || !timeFrame?.endDate) return;

    setIsFetching(true);
    fetchDriverAppStats({
      from: timeFrame.startDate,
      to: timeFrame.endDate,
      businessUnit: businessUnit ?? undefined,
      supplierId: selectedSupplier ? Number(selectedSupplier.key) : undefined,
      licensePlate: selectedTruck?.key,
    })
      .then((data) => {
        setOrders(data.orders);
        setSummary(data.summary);
      })
      .catch(() => {
        setOrders([]);
        setSummary(null);
      })
      .finally(() => setIsFetching(false));
  }, [timeFrame?.startDate, timeFrame?.endDate, businessUnit, selectedSupplier, selectedTruck]);

  const filteredOrders = orders.filter((order) => {
    if (flagFilter === 'flagged' && order.flags.length === 0) {
      return false;
    }
    return true;
  });

  const dataMapper = (order: DriverAppStatsOrder) => {
    return [
      <Link to={`/internal/orders/${order.id}/general`}>{order.orderNumber || '-'}</Link>,
      <Link to={`/internal/suppliers/${order.supplierId}/general`}>{order.supplierName}</Link>,
      order.truckId ? (
        <Link to={`/internal/suppliers/${order.supplierId}/trucks/${order.truckId}`}>{order.licensePlate || '-'}</Link>
      ) : (
        <span>{order.licensePlate || '-'}</span>
      ),
      <span>{order.driverName || '-'}</span>,
      <span>{order.loadDate ? new Date(order.loadDate).toLocaleDateString() : '-'}</span>,
      <span>{order.unloadDate ? new Date(order.unloadDate).toLocaleDateString() : '-'}</span>,
      <AppUsageIcon used={order.loadedViaApp} />,
      <AppUsageIcon used={order.unloadedViaApp} />,
      <AppUsageIcon used={order.cmrUploadedViaApp} />,
      <div className="flex flex-wrap gap-1">
        {order.flags.map((flag) => (
          <FlagBadge key={flag} flag={flag} />
        ))}
      </div>,
    ];
  };

  return (
    <div className="flex flex-col gap-4">
      <h2 className="heading-two">Driver App Gebruik</h2>

      {isFetching && <DelayedSpinner size={8} className="flex justify-center py-8" />}

      {!isFetching && summary && (
        <>
          <div className="flex flex-wrap gap-4">
            <StatCard
              variant="compact"
              className="flex-1 min-w-44"
              label="Totaal orders"
              value={`${summary.totalOrders}/${summary.totalOrders}`}
              subValue={appUsageShare(summary.totalOrders, summary.totalOrders)}
            />
            <StatCard
              variant="compact"
              className="flex-1 min-w-44"
              label="Laden via app"
              value={`${summary.loadedViaApp}/${summary.totalOrders}`}
              subValue={appUsageShare(summary.loadedViaApp, summary.totalOrders)}
            />
            <StatCard
              variant="compact"
              className="flex-1 min-w-44"
              label="Lossen via app"
              value={`${summary.unloadedViaApp}/${summary.totalOrders}`}
              subValue={appUsageShare(summary.unloadedViaApp, summary.totalOrders)}
            />
            <StatCard
              variant="compact"
              className="flex-1 min-w-44"
              label="CMR via app"
              value={`${summary.cmrUploadedViaApp}/${summary.totalOrders}`}
              subValue={appUsageShare(summary.cmrUploadedViaApp, summary.totalOrders)}
            />
            <StatCard
              variant="compact"
              className="flex-1 min-w-44"
              label="Geflagde orders"
              value={summary.flaggedOrders}
              tone="negative"
            />
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full max-w-64">
              <div className="label-text mb-1.5">Vervoerder</div>
              <SimpleSelect
                items={supplierItems}
                selectedItem={selectedSupplier}
                onSelect={setSelectedSupplier}
                isOptional={true}
                isDisabled={suppliersLoading}
              />
            </div>

            <div className="w-full max-w-48">
              <div className="label-text mb-1.5">Nummerplaat</div>
              <SimpleSelect
                items={truckItems}
                selectedItem={selectedTruck}
                onSelect={setSelectedTruck}
                isOptional={true}
                isDisabled={!selectedSupplier}
              />
            </div>

            <div className="flex gap-2">
              <Button color={flagFilter === 'all' ? 'primary' : 'secondary'} onTrigger={() => setFlagFilter('all')}>
                Alle orders
              </Button>
              <Button
                color={flagFilter === 'flagged' ? 'primary' : 'secondary'}
                onTrigger={() => setFlagFilter('flagged')}
              >
                Alleen geflagd ({summary.flaggedOrders})
              </Button>
            </div>
          </div>

          <Table
            headers={[
              { id: 'orderNumber', name: 'Order Nr', width: '9em' },
              { id: 'supplier', name: 'Vervoerder' },
              { id: 'licensePlate', name: 'Nummerplaat' },
              { id: 'driver', name: 'Chauffeur' },
              { id: 'loadDate', name: 'Datum laden' },
              { id: 'unloadDate', name: 'Datum lossen' },
              { id: 'loadedViaApp', name: 'Laden via app', width: '7em' },
              { id: 'unloadedViaApp', name: 'Lossen via app', width: '7em' },
              { id: 'cmrViaApp', name: 'CMR via app', width: '7em' },
              { id: 'flags', name: 'Flags' },
            ]}
            data={filteredOrders}
            mapData={dataMapper}
            idKey="id"
            isMultiSelect={false}
          />

          {filteredOrders.length === 0 && <div className="text-sm font-medium text-fg">Geen orders gevonden</div>}
        </>
      )}
    </div>
  );
}
